import type { CSSProperties } from 'react'

/**
 * 像素图标组件 —— DESIGN.md §10 图标规范的落地实现
 *
 * - 统一 16×16 网格、直角像素边缘（shape-rendering: crispEdges）
 * - 至多两个颜色层级：主层级 currentColor + 次层级 currentColor @ 45% 透明
 * - 颜色跟随文字颜色（currentColor），大小用 size 控制
 * - 用法：<Icon name="scroll" size={20} label="答案之书" />
 *         <Icon name="sparkle" size={16} aria-hidden />
 */

/** 像素矩形：[x, y, w, h]，坐标在 16×16 网格上 */
type Rect = [number, number, number, number]

interface IconDef {
  /** 主层级像素块 */
  a?: Rect[]
  /** 次层级像素块（半透明，做阴影/细节） */
  b?: Rect[]
  /** 带孔洞的形状路径（fill-rule: evenodd） */
  d?: string
}

/** 对角线：从 (x0,y0) 步进到 (x1,y1)，每步一个 1×1 像素（含两端） */
function diag(x0: number, y0: number, x1: number, y1: number): Rect[] {
  const out: Rect[] = []
  const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))
  const sx = Math.sign(x1 - x0)
  const sy = Math.sign(y1 - y0)
  for (let i = 0; i <= steps; i++) out.push([x0 + sx * i, y0 + sy * i, 1, 1])
  return out
}

/** 加粗：整组像素按 (dx,dy) 复制一份 */
function thicken(cells: Rect[], dx: number, dy: number): Rect[] {
  return cells.map(([x, y]) => [x + dx, y + dy, 1, 1] as Rect)
}

const checkCells = [...diag(2, 8, 5, 11), ...diag(6, 10, 13, 3)]
const crossCells = [...diag(3, 3, 12, 12), ...diag(12, 3, 3, 12)]
const starCells: Rect[] = [
  [7, 1, 2, 3], [5, 4, 6, 2], [1, 6, 14, 2], [3, 8, 10, 2],
  [2, 10, 4, 3], [10, 10, 4, 3], [6, 10, 4, 1],
]

/** 图标注册表：全部手工绘制于 16×16 网格 */
const ICONS = {
  /* —— 系统 —— */
  sun: {
    a: [[6, 6, 4, 4], [7, 1, 2, 2], [7, 13, 2, 2], [1, 7, 2, 2], [13, 7, 2, 2]],
    b: [[3, 3, 1, 1], [12, 3, 1, 1], [3, 12, 1, 1], [12, 12, 1, 1]],
  },
  moon: {
    d: 'M5 2h6v1h2v1h1v2h1v4h-1v2h-1v1h-2v1H5v-1H3v-1H2v-2H1V6h1V4h1V3h2zM9 4h4v1h1v4h-1v1H9z',
    b: [[11, 6, 2, 1]],
  },
  gear: {
    d: 'M3 3h10v10H3zM6 6h4v4H6z',
    a: [
      [6, 0, 4, 3], [6, 13, 4, 3], [0, 6, 3, 4], [13, 6, 3, 4],
      [1, 1, 3, 3], [12, 1, 3, 3], [1, 12, 3, 3], [12, 12, 3, 3],
    ],
  },
  bulb: {
    a: [[5, 2, 6, 5], [6, 7, 4, 1], [6, 8, 4, 2]],
    b: [[7, 0, 2, 1], [2, 2, 2, 1], [12, 2, 2, 1]],
  },
  lock: {
    d: 'M3 7h10v8H3zM7 10h2v3H7z',
    a: [[5, 2, 6, 1], [5, 3, 1, 4], [10, 3, 1, 4]],
  },
  warn: {
    d: 'M7 5h2v4H7zM7 11h2v2H7z',
    a: [[7, 2, 2, 2], [6, 4, 4, 2], [5, 6, 6, 2], [4, 8, 8, 2], [3, 10, 10, 2], [2, 12, 12, 2]],
  },
  question: {
    a: [[5, 2, 6, 1], [4, 3, 1, 2], [10, 3, 1, 3], [9, 6, 2, 1], [8, 7, 2, 2], [8, 10, 2, 2]],
  },

  /* —— 对错 / 星 —— */
  check: { a: [...checkCells, ...thicken(checkCells, 0, 1)] },
  cross: { a: [...crossCells, ...thicken(crossCells, 1, 0)] },
  star: { a: starCells },
  starEmpty: { b: starCells },

  /* —— 导航入口（DESIGN §10.2 语义表）—— */
  scroll: {
    a: [[4, 3, 8, 10], [3, 1, 10, 2], [3, 13, 10, 2]],
    b: [[6, 6, 4, 1], [6, 8, 4, 1]],
  },
  chart: {
    a: [[2, 8, 3, 6], [7, 4, 3, 10], [12, 1, 3, 13], [1, 14, 14, 1]],
  },
  target: {
    d: 'M2 2h12v12H2zM4 4h8v8H4z',
    a: [[7, 7, 2, 2], [7, 0, 2, 2], [7, 14, 2, 2], [0, 7, 2, 2], [14, 7, 2, 2]],
  },
  chat: {
    a: [[2, 2, 12, 8], [4, 10, 2, 1], [3, 11, 2, 1], [2, 12, 2, 1]],
    b: [[5, 5, 1, 1], [8, 5, 1, 1], [11, 5, 1, 1]],
  },
  inbox: {
    a: [[2, 5, 2, 7], [12, 5, 2, 7], [2, 12, 12, 2], [6, 9, 4, 1]],
  },
  note: {
    a: [...diag(4, 10, 11, 3), ...thicken(diag(4, 10, 11, 3), 1, 1), [2, 11, 2, 2], [1, 13, 1, 1]],
  },
  flame: {
    a: [[7, 1, 2, 2], [6, 3, 4, 2], [5, 5, 6, 4], [4, 9, 8, 3], [6, 12, 4, 2]],
    b: [[7, 6, 2, 3]],
  },

  /* —— 卡片题型 —— */
  book: {
    a: [[4, 2, 9, 12]],
    b: [[4, 2, 2, 12], [10, 2, 2, 3]],
  },
  sword: {
    a: [...diag(3, 12, 12, 3), [4, 9, 1, 1], [6, 11, 1, 1]],
  },
  magnifier: {
    d: 'M4 2h6v1h2v1h1v6h-1v1h-1v1H4v-1H3v-1H2V4h1V3h1zM5 5h4v1h1v3h-1v1H5v-1H4V6h1z',
    a: [...diag(11, 11, 14, 14)],
  },
  timer: {
    d: 'M4 4h8v10H4zM6 6h4v6H6z',
    a: [[7, 1, 2, 2], [7, 3, 2, 1]],
    b: [[8, 7, 1, 2], [7, 9, 2, 1]],
  },
  brain: {
    a: [[5, 2, 6, 2], [3, 4, 10, 7], [5, 11, 6, 2]],
    b: [[7, 3, 2, 9], [4, 7, 8, 1]],
  },

  /* —— 杂项 —— */
  page: {
    a: [[4, 2, 8, 12]],
    b: [[10, 2, 2, 2], [6, 5, 4, 1], [6, 7, 4, 1], [6, 9, 4, 1], [6, 11, 3, 1]],
  },
  card: {
    a: [[4, 2, 8, 12]],
    b: [[6, 4, 4, 3], [7, 9, 2, 2]],
  },
  trophy: {
    a: [[3, 2, 10, 2], [4, 4, 8, 4], [6, 8, 4, 2], [7, 10, 2, 2], [5, 12, 6, 2]],
    b: [[1, 3, 2, 3], [13, 3, 2, 3]],
  },
  sparkle: {
    a: [[7, 2, 2, 12], [2, 7, 12, 2]],
    b: [[4, 4, 1, 1], [11, 4, 1, 1], [4, 11, 1, 1], [11, 11, 1, 1]],
  },
  teacup: {
    // 盖碗 + 碗托, 次层级是两缕茶烟
    a: [[3, 5, 9, 1], [3, 6, 9, 5], [4, 11, 7, 1], [3, 13, 9, 1], [12, 6, 2, 1], [13, 7, 1, 2], [12, 9, 2, 1]],
    b: [[5, 1, 1, 3], [8, 2, 1, 2]],
  },
  broom: {
    // 扫帚: 柄 + 散开的草 (用于「清空学习」按钮)
    a: [
      [7, 1, 2, 1], [7, 2, 2, 8], // 柄
      [5, 10, 1, 4], [6, 10, 1, 5], [7, 10, 2, 5], [8, 10, 1, 5], [9, 10, 1, 4], // 散草
      [4, 13, 1, 2], [10, 13, 1, 2], // 边缘草
    ],
    b: [[3, 11, 1, 1], [11, 11, 1, 1]], // 扬起的灰屑
  },
} satisfies Record<string, IconDef>

export type IconName = keyof typeof ICONS

interface IconProps {
  name: IconName
  /** 像素尺寸（宽=高），默认 16 */
  size?: number
  /** 提供 label → role="img" + aria-label；不提供则默认 aria-hidden */
  label?: string
  className?: string
  style?: CSSProperties
  'aria-hidden'?: boolean
}

function Icon({ name, size = 16, label, className = '', style, 'aria-hidden': hidden }: IconProps) {
  const def: IconDef = ICONS[name]
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className={`inline-block align-[-2px] flex-shrink-0 ${className}`}
      style={style}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : (hidden ?? true)}
    >
      {def.d && <path d={def.d} fill="currentColor" fillRule="evenodd" />}
      {def.a?.map(([x, y, w, h], i) => (
        <rect key={i} x={x} y={y} width={w} height={h} fill="currentColor" />
      ))}
      {def.b?.map(([x, y, w, h], i) => (
        <rect key={`b${i}`} x={x} y={y} width={w} height={h} fill="currentColor" fillOpacity={0.45} />
      ))}
    </svg>
  )
}

export default Icon
